import type { RouteLocationRaw } from 'vue-router';

export type StandardButtonProps = {
  /**
   * The label to be displayed in the button.
   */
  label: string;
  /**
   * The title to be displayed when hovering over the button.
   */
  title?: string;
  /**
   * If true, this button will be disabled and a loading spinner will be shown
   */
  loading?: boolean;
  disabled?: boolean;
  /**
   * If true, the button will span the full width of its container
   */
  fluid?: boolean;
  /**
   * If true (default), long label text truncates with an ellipsis when horizontal space is limited.
   * Pass `false` to keep content-sized, non-shrinking buttons (typical in tight toolbars).
   * Icon-only buttons never truncate.
   * `title` defaults to `label` for hover unless `title` is set.
   */
  shouldTruncateLabel?: boolean;
  /**
   * If true, the button will have rounded pill styling
   */
  rounded?: boolean;
  /**
   * Button size: platform standard button default size is small. PrimeVue default size is normal.
   */
  size?: ButtonSize;
  /**
   * Button color
   */
  severity?: ButtonSeverity;
  /**
   * Visual style: solid(PrimeVue default), outlined, text.
   */
  variant?: ButtonVariant;
  /**
   * Only icon to be displayed in the button
   * This MUST be used when button has only icon and no label
   */
  icon?: string;
  /**
   * Icon to be displayed on the left/right side of the button with label
   * This MUST be used when button has both icon and label
   * Do not use with `icon` (icon-only).
   */
  leftIcon?: string;
  rightIcon?: string;
  dataTestid?: string;
  to?: RouteLocationRaw;
  href?: string;

  /**
   * Set to `submit` or `reset` when the button lives inside a
   * `<form>` so Enter submits the form and `@submit` / `@reset` handlers run.
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Link `target` when `to` or `href` is set. Default is `_blank` to open in a new tab.
   */
  target?: '_blank' | '_self' | '_parent' | '_top';
  // Custom class prop for the button
  classProp?: Record<string, unknown> | string | string[];
};

/**
 * "Severity" — represents button color in prime vue
 * It selects the button token branch in the PrimeVue preset.
 * Built-in values map to PrimeVue `severity` and `--p-button-*` tokens.
 * Custom values (from `accent` down) use ComponentColorSchemes.button via per-scheme CSS classes;
 * they are not forwarded as PrimeVue `severity`.
 * This pairs with `ButtonVariant` for full styling.
 */
export enum ButtonSeverity {
  /**
   * primary: old bootstrap info color needs to map to PRIMARY
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  PRIMARY = 'primary',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  SECONDARY = 'secondary',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  SUCCESS = 'success',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  INFO = 'info',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  WARN = 'warn',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  HELP = 'help',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  DANGER = 'danger',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  CONTRAST = 'contrast',

  // Custom severities with no PrimeVue base-CSS branch — tokens live under
  // `ComponentColorSchemes.button` in the preset `extend` block.
  //  * -inverse is for high-contrast counterparts of the default tokens.
  // Use only on surfaces that invert relative to the current theme (dark surfaces in light mode, light surfaces in dark mode).
  // Specifically used in toolbar and analytics toolbar component.

  /**
   * accent: Use for interactive elements that commit a lasting change (save, clone, create).
   * old bootstrap primary color needs to map to ACCENT
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  ACCENT = 'accent',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  ACCENT_INVERSE = 'accent-inverse',
  /**
   * supported Variant: SOLID, OUTLINED, TEXT
   */
  PRIMARY_INVERSE = 'primary-inverse',
  /**
   * variant: Use when the control refers to a specific variant of the selected item.
   * This variant is for button severity not how button is rendered.
   * supported Variant: TEXT
   */
  VARIANT = 'variant',
  /**
   * supported Variant: TEXT
   */
  VARIANT_INVERSE = 'variant-inverse',
  /**
   * supported Variant: TEXT
   */
  KNOWLEDGE_BASE_INVERSE = 'knowledge-base-inverse',
  /**
   * supported Variant: TEXT
   */
  ICON_SIDEBAR = 'iconsidebar',
  /**
   * item-under-analysis: Use when the control refers to the item currently selected for analysis(i.e on analytics tool bar).
   * supported Variant: TEXT
   */
  ITEM_UNDER_ANALYSIS = 'item-under-analysis',
  /**
   * supported Variant: TEXT
   */
  ITEM_UNDER_ANALYSIS_INVERSE = 'item-under-analysis-inverse',
  /**
   * supported Variant: TEXT
   */
  SENTENCE_TITLE = 'sentence-title',
}

/**
 * Custom severities styled via per-scheme classes (e.g. `standard-button--text-sentence-title`)
 * and `--p-component-color-schemes-button-*` CSS variables (not PrimeVue `severity`).
 */
export const customButtonColorSchemeSeverity: ReadonlySet<ButtonSeverity> = new Set([
  ButtonSeverity.ACCENT,
  ButtonSeverity.ACCENT_INVERSE,
  ButtonSeverity.PRIMARY_INVERSE,
  ButtonSeverity.VARIANT,
  ButtonSeverity.VARIANT_INVERSE,
  ButtonSeverity.KNOWLEDGE_BASE_INVERSE,
  ButtonSeverity.ICON_SIDEBAR,
  ButtonSeverity.ITEM_UNDER_ANALYSIS,
  ButtonSeverity.ITEM_UNDER_ANALYSIS_INVERSE,
  ButtonSeverity.SENTENCE_TITLE,
]);

/**
 * How the button is rendered. Color is `ButtonSeverity`.
 *
 * They are mutually exclusive.
 *
 * PrimeVue uses booleans (`outlined`, `text`) instead of a variant prop. This enum
 * folds those flags into one `variant` on StandardButton.
 */
export enum ButtonVariant {
  /**
   * SOLID - a filled button, primevue default case (also default case for BootstrapVue)
   */
  SOLID = 'solid',
  /**
   * OUTLINED - a button with no filled color but does have a colored outline (corresponds to the 'outline' button variant we created for BootstrapVue)
   */
  OUTLINED = 'outlined',
  /**
   * TEXT - a button that has no border or fill (roughly corresponds to SentenceDropdown)
   */
  TEXT = 'text',
}

/**
 * Button size is to control padding, font-size, and icon-only width.
 *
 * PrimeVue’s base size is unnamed: only `small` and `large` are real `size` props.
 * `NORMAL` means PrimeVue uses `button.root` tokens
 * (`--p-button-padding-*`, `--p-button-icon-only-width`).
 *
 * `SMALL` / `LARGE` map to `p-button-sm` / `p-button-lg` and `--p-button-sm-*` / `--p-button-lg-*`.
 *
 * StandardButton defaults to `SMALL` for the whole platform, not PrimeVue’s base.
 */
export enum ButtonSize {
  SMALL = 'small',
  NORMAL = 'normal',
  LARGE = 'large',
}

export enum ButtonIconPosition {
  LEFT = 'left',
  RIGHT = 'right',
}

export type StandardButtonIconParams = {
  icon?: string;
  leftIcon?: string;
  rightIcon?: string;
};

/** From Figma design system documentation only — not a runtime component prop. Mainly used for testing in design sandbox. */
export enum ButtonVisualState {
  IDLE = 'idle',
  HOVER = 'hover',
  FOCUS = 'focus',
  ACTIVE = 'active',
  DISABLED = 'disabled',
  LOADING = 'loading',
}
